import React from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import LeadershipReviewSummaryTable from './LeadershipReviewSummaryTable';
import DetailedDevelopmentPlan from './DetailedDevelopmentPlan';

const LeadershipReviewComponent = ({ formik, cellStyle, cellStyleW, headerStyle, renderInputAreaUptoTen, reviewData, roleName }) => {

    // Helper to calculate average for a section
    const calculateSectionAverage = (sectionId) => {
        // We assume formik.values.leadership_reviews_and_development_plan.competencies[sectionId] exists
        // structure: { scores: { [questionIndex]: { self: 0, supervisor: 0 } } }
        
        const sectionData = formik.values.leadership_reviews_and_development_plan?.competencies?.[sectionId];
        if (!sectionData || !sectionData.scores) return 0;

        let totalSelf = 0;
        let totalSup = 0;
        let count = 0;

        // Iterate through questions in reviewData for this section
        const sectionDef = reviewData.find(s => s.id === sectionId);
        if(!sectionDef) return 0;

        sectionDef.questions.forEach((_, index) => {
            const scores = sectionData.scores[index];
            if (scores) {
                if (scores.self) totalSelf += Number(scores.self);
                if (scores.supervisor) totalSup += Number(scores.supervisor);
                count++;
            }
        });

        if (count === 0) return 0; 
        
        // Let's return a string like "Self: X, Sup: Y" or composite average
        // Valid values count for self and sup
        let selfCount = 0;
        let supCount = 0;
        sectionDef.questions.forEach((_, index) => {
             const scores = sectionData.scores[index];
             if(scores && scores.self) selfCount++;
             if(scores && scores.supervisor) supCount++;
        });

        const avgSelf = selfCount > 0 ? (totalSelf / selfCount).toFixed(1) : 0;
        const avgSup = supCount > 0 ? (totalSup / supCount).toFixed(1) : 0;

        return { avgSelf, avgSup };
    };

    const renderScoreInput = (sectionId, questionIndex, role) => {
        // role is 'self' or 'supervisor'
        const fieldName = `leadership_reviews_and_development_plan.competencies.${sectionId}.scores.${questionIndex}.${role}`;
        
        // Ensure the path exists in formik values, or handle it via setFieldValue safely
        // But renderInputAreaUptoTen expects a fieldName.
        // We need to ensure initialValues are set in the parent.
        
        return renderInputAreaUptoTen(fieldName, '', 'number', formik.values.leadership_reviews_and_development_plan?.competencies?.[sectionId]?.scores?.[questionIndex]?.[role] || '');
    };

    // Show 'Sr' in the title if this is the Sr Project Manager review
    const isSrReview = roleName === 'Sr Project Manager';
    const isCEMReview = roleName === 'Interior Designer';
    const isVpReview = roleName === 'VP of Construction Operations';

    const getReviewTitle = () => {
        if (isVpReview) return 'LIDA Homes VP of Construction Operations Leadership Review & Development Plan';
        if (isCEMReview) return 'LIDA Homes Client Experience Manager & Interior Design Leadership Review & Development Plan';
        if (isSrReview) return 'LIDA Homes Sr. Project Manager Leadership Review & Development Plan';
        return 'LIDA Homes Leadership Review & Development Plan';
    };

    return (
        <div className="lida-review-container">
             <div className='surface-card p-4 m-4 border-round shadow-1'>
                <h2 className="text-2xl font-bold text-900 mb-3">
                    {getReviewTitle()}
                </h2>
                
                <div className="text-700 text-sm" style={{ lineHeight: '1.6' }}>
                    <p className="mb-2"><strong>Purpose: </strong>
                    {isVpReview
                      ? 'The Vice President of Construction Operations is accountable for the performance, consistency, and scalability of LIDA’s entire delivery organization. LIDA Homes’ brand, profitability, and client experience depend on this role setting standards, building systems, developing leaders, and ensuring predictable execution across the company. The VP of Construction Operations owns the performance of the construction department as a system, not just individual projects. This review exists to answer one question: What kind of executive leader is this person becoming? This is not a task checklist. It is a leadership and organizational capability standard.'
                      : (isCEMReview
                        ? 'The Client Experience Manager & Interior Designer is the key to fulfilling the promises we make to our customers. LIDA Homes’ brand and financial performance rely on the proper execution of this role. The Client Experience Manager & Interior Designer oversees all aspects of the client experience and selections process and is responsible for ensuring decisions and documentation support projects being completed on budget, on schedule, and within scope, while delivering a WOW client experience and protecting LIDA standards. This review exists to answer one question: What kind of Project Leader is this person becoming? This is not a task checklist. It is a leadership and capability standard.'
                        : (isSrReview
                            ? 'The Sr. Project Manager is the key to fulfilling the promises we make to our customers. LIDA Homes’ brand and financial performance rely on the proper execution of this role. The Sr. Project Manager oversees all aspects of the building process and is responsible for ensuring that all projects are completed on budget, on schedule, and within scope, while delivering a WOW client experience and protecting LIDA standards. This review exists to answer one question: What kind of Project Leader is this person becoming? This is not a task checklist. It is a leadership and capability standard.'
                            : 'The Project Manager is the key to fulfilling the promises we make to our customers. LIDA Homes’ brand and financial performance rely on the proper execution of this role. The Project Manager oversees all aspects of the building process and is responsible for ensuring the project is completed on budget, on schedule, and within scope, while delivering a WOW client experience and protecting LIDA standards. This review exists to answer one question: What kind of Project Leader is this person becoming? This is not a task checklist. It is a leadership and capability standard.'))
                    }
                    </p>

                    <p className="mb-2"><strong>How This Review Is Used: </strong>
                    This is a two-part review. First, the {isVpReview ? 'VP' : (isCEMReview ? 'Client Experience Manager & Interior Designer' : (isSrReview ? 'Sr. Project Manager' : 'Project Manager'))} completes a self-review. Second, the Supervisor completes the same review independently. Each line item is scored from 1 to 10. The score for each Competency is the AVERAGE of its line items. The two scores are then compared and discussed to align on strengths, gaps, and the development plan.</p>
                    
                    <p className="mb-2"><strong>Cadence:</strong> This review is completed once per year and is reviewed quarterly to discuss progress against the Development Plan and to adjust priorities. Quarterly check-ins do not require re-scoring every line item unless the Supervisor chooses to do so.</p>
                    
                    <p className="mb-2"><strong>Important:</strong> Some statements below are directly evidenced by weekly scorecards and performance points. Others reflect leadership judgment, maturity, and consistency that cannot be reduced to a weekly checkbox. Both matter.</p>
                </div>
                
                <hr className="surface-border my-3"/>

                <div className="text-700 text-sm" style={{ lineHeight: '1.6' }}>
                    <p className="mb-2 font-bold text-900 text-lg">Applies to Every Line Item</p>
                    <p className="mb-2">8 = Meets the Standards (“Good Enough”). 9–10 = Excellent. 1–7 = Below Standard and must be addressed.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <p className="mb-1"><span className="text-yellow-600 font-bold">1 — Unacceptable:</span> Actively harming outcomes. Creates risk to clients, projects, or team. Fundamentally not meeting the requirements of the role.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">2 — Critical Risk:</span> Regularly creates serious problems. Requires constant supervision. Unsafe to operate independently in this area.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">3 — Major Deficiency:</span> Frequently below expectations. Creates avoidable issues through poor judgment or lack of discipline. Needs a formal improvement plan.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">4 — Serious Gap:</span> Some capability, but performance is unreliable. Still creates significant risk or rework. Cannot be trusted in this area yet.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">5 — Inconsistent:</span> Sometimes performs well, sometimes poorly. Performance depends on conditions and support. Still below LIDA standard.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">6 — Developing:</span> Improving and shows promise. Handles routine situations but struggles with complexity or pressure. Still below LIDA standard.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">7 — Almost There:</span> Generally competent, but still inconsistent or reactive. Fewer problems, but still too many surprises. Close to standard, but not there yet.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">8 — Meets Standard:</span> Consistent, reliable, professional. Handles this area without drama or surprises. This is the minimum acceptable score for a fully competent {isVpReview ? 'VP' : (isCEMReview ? 'CEM/ID' : 'PM')}.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">9 — Excellent / Role Model:</span> Among the strongest at LIDA in this area. Others learn from them. Raises the standard.</p>
                        <p className="mb-1"><span className="text-yellow-600 font-bold">10 — Exceptional / Organizational Benchmark:</span> Rare. Builds systems, people, and standards. Makes the company better beyond their own {isVpReview ? 'projects' : (isCEMReview ? 'projects' : 'projects')}.</p>
                    </div>
                </div>

                <hr className="surface-border my-3"/>

                <div className="text-700 text-sm" style={{ lineHeight: '1.6' }}>
                    <p className="mb-2 font-bold text-900 text-lg">Rules</p>
                    {isVpReview ? (
                        <ul className="list-disc pl-5">
                            <li>Any competency with an average below 8 must be addressed in the Development Plan.</li>
                            <li>Any competency with an average below 6 requires a formal Development Plan.</li>
                            <li>If the VP has more than two competencies averaging below 8, this becomes a major leadership priority for the year.</li>
                            <li>Scores should be supported by examples from real projects, including weekly scorecards and performance points where applicable.</li>
                        </ul>
                    ) : (isCEMReview ? (
                        <ul className="list-disc pl-5">
                            <li>Any competency with an average below 8 must be addressed in the Development Plan.</li>
                            <li>Any competency with an average below 6 requires a formal Development Plan.</li>
                            <li>If a CEM/ID has more than two competencies averaging below 8, this becomes a major leadership priority for the year.</li>
                            <li>Scores should be supported by examples from real projects, including weekly scorecards and performance points where applicable.</li>
                        </ul>
                    ) : (
                        <ul className="list-disc pl-5">
                            <li>Any competency with an average below 8 must be addressed in the Development Plan.</li>
                            <li>Any competency with an average below 6 requires a formal Development Plan.</li>
                            <li>If a {isSrReview ? 'Sr. PM' : 'PM'} has more than two competencies averaging below 8, this becomes a major leadership priority for the year.</li>
                            <li>Scores should be supported by examples from real projects, including weekly scorecards and performance points where applicable.</li>
                        </ul>
                    ))}
                </div>
            </div>

            {reviewData.map((section) => {
                 const averages = calculateSectionAverage(section.id);

                return (
                <div key={section.id} className="section-container mb-4 mx-4">
                    <div className="bg-gray-200 p-2 border border-gray-300">
                        <h3 className="font-bold text-lg">{section.id}. {section.title}</h3>
                        <p className="italic text-sm text-gray-700">{section.definition}</p>
                    </div>

                    <div className="px-4 py-3 overflow-x-auto">
                        <table style={{ borderCollapse: "collapse", width: "100%" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...headerStyle, width: "50px" }}>#</th>
                                    <th style={{ ...headerStyle, textAlign: 'left', paddingLeft: '10px' }}>Behavior / Competency</th>
                                    <th style={{ ...headerStyle, width: "100px" }}>Self Score<br/>(1-10)</th>
                                    <th style={{ ...headerStyle, width: "100px" }}>Supervisor Score<br/>(1-10)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {section.questions.map((question, qIndex) => (
                                    <tr key={qIndex}>
                                        <td style={{ ...cellStyleW, textAlign: 'center' }}>{qIndex + 1}</td>
                                        <td style={{ ...cellStyleW, padding: '8px' }}>{question}</td>
                                        <td style={{ ...cellStyleW }}>
                                            {renderScoreInput(section.id, qIndex, 'self')}
                                        </td>
                                        <td style={{ ...cellStyleW }}>
                                            {renderScoreInput(section.id, qIndex, 'supervisor')}
                                        </td>
                                    </tr>
                                ))}
                                {/* Averages Row */}
                                <tr className="bg-yellow-50 font-bold">
                                    <td colSpan={2} style={{ ...cellStyleW, textAlign: 'right', paddingRight: '10px', height: '40px', verticalAlign: 'middle' }}>
                                        Section Average:
                                    </td>
                                    <td style={{ ...cellStyleW, textAlign: 'center', height: '40px', verticalAlign: 'middle' }}>
                                        {averages.avgSelf}
                                    </td>
                                    <td style={{ ...cellStyleW, textAlign: 'center', height: '40px', verticalAlign: 'middle' }}>
                                        {averages.avgSup}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="mt-2">
                             <h4 className="font-bold text-sm mb-1">Comments & Examples:</h4>
                             <InputTextarea
                                value={formik.values.leadership_reviews_and_development_plan?.competencies?.[section.id]?.comments || ''}
                                onChange={(e) => formik.setFieldValue(`leadership_reviews_and_development_plan.competencies.${section.id}.comments`, e.target.value)}
                                rows={3}
                                className="w-full p-2 border border-gray-300"
                                placeholder={`Enter specific examples for ${section.title}...`}
                             />
                        </div>
                    </div>
                </div>
            )})}
            
            {/* Leadership Review Summary Table */}
            <div className='surface-card p-4 mx-4 mt-6 mb-3 border-round shadow-1'>
                <h2 className="text-xl font-bold text-900 mb-2">
                    Summary & Prioritization
                </h2>
                <p className="text-700">Review the gaps and prioritize 3 key areas for development.</p>
            </div>
            <div className="px-4 py-3">
                <LeadershipReviewSummaryTable formik={formik} reviewData={reviewData} />
            </div>

            {/* Development Plan Section */}
            <div className='surface-card p-4 mx-4 mt-6 mb-4 border-round shadow-1'>
                <h2 className="text-xl font-bold text-900 mb-2">
                    {isCEMReview ? '12-Month Development Plan (Leadership Improvement Plan)' : '12-Month Development Plan'}
                </h2>
                <p className="text-700">
                    {isCEMReview 
                     ? 'Purpose: This section converts the results of the Competency Review into a focused, disciplined, measurable development plan. The goal is not to work on everything. The goal is to identify the few highest-impact gaps and fix them permanently.'
                     : 'Identify 3 key areas specifically from the gaps above.'}
                </p>
            </div>

            <div className="px-4 py-3">
                 {[0, 1, 2].map((i) => {
                    const summaryOrder = formik.values.leadership_reviews_and_development_plan?.summary_order || [];
                    let autoCompetencyName = '';
                    if (summaryOrder.length > i) {
                        const sectionId = summaryOrder[i];
                        const section = reviewData.find(s => s.id === sectionId);
                        if (section) autoCompetencyName = section.title;
                    }

                    return (
                        <DetailedDevelopmentPlan 
                            key={i} 
                            formik={formik} 
                            index={i} 
                            cellStyle={cellStyle} 
                            cellStyleW={cellStyleW} 
                            autoCompetencyName={autoCompetencyName}
                        />
                    );
                 })}
            </div>
        </div>
    );
};

export default LeadershipReviewComponent;
